import {
    CurvesFillingType,
    FillingType,
    FillingValueType,
    MinMaxValueType,
    WidthType,
} from '@/components/WellLoggingOperations/types';
import type {
    ColumnsVisibility,
    ColumnsSizing,
    CurveDisplaySettings,
    TabletColumns,
} from '@/components/WellLoggingOperations/types';

// wellServices: 'Сервисные работы на скважине',
// geoObjects: 'Геологические объекты (РИГИС и ФГИ)',
// geoObjectsRigis: 'Геологические объекты (РИГИС)',
// geoObjectsFgi: 'Геологические объекты (ФГИ)',
// lithology: 'Литология/Насыщение',
// paramsHistogram: 'Гистограммы параметров',
// cementometry: 'Цементометрия',
// core: 'Керн',
// testing: 'Опробования пластов',
// flowmetry: 'Потокометрия',
// carrotage: 'Каротаж',
// fluidTest: 'Гидродинамические исследования',
// sample: 'ГРП',
// cementBondLogging: 'РИГИС остаточной нефтенасыщенности',
// stratigraphy: 'Стратиграфия',
// tech: 'Тех. характеристики',
// thicknessInterlayer: 'Эффективная толщина',

/*
    Видимость колонок по умолчанию
*/
export const defaultColumnsVisibility: ColumnsVisibility = {
    wellServices: true,
    allGeoObjects: true,
    geoObjects: true,
    lithology: true,
    thicknessInterlayer: true,
    stratigraphy: true,
    paramsHistogram: false,
    cementBondLogging: false,
    cementometry: false,
    innk: false,
    tech: false,
    tech_columns: false,
    tech_bridges: false,
    tech_cement: false,
    carrotage: true,
    core: false,
    coreParamsHistogram: false,
    testing: false,
    flowmetry: false,
    fluidTest: false,
    sample: false,
    stratigraphy_system: true,
    stratigraphy_overhorizon: true,
    stratigraphy_horizon: true,
    stratigraphy_department: true,
    stratigraphy_tier: true,
    tracers: false,
};

export const defaultColumnsOrder: TabletColumns[] = [
    'wellServices',
    'allGeoObjects',
    'geoObjects',
    'lithology',
    'thicknessInterlayer',
    'paramsHistogram',
    'stratigraphy',
    'core',
    'coreParamsHistogram',
    'tech',
    'testing',
    'carrotage',
    'flowmetry',
    'innk',
    'cementBondLogging',
    'cementometry',
    'fluidTest',
    'sample',
    'tracers',
];

export const defaultRigisColumnsOrder: TabletColumns[] = [
    'geoObjects',
    'lithology',
    'thicknessInterlayer',
    'paramsHistogram',
];

export const defaultRigisColumnsCount = 3;

/*
    Значения размеров колонок по умолчанию
*/
export const defaultColumnsSizing: ColumnsSizing = {
    /*
        Каротаж
    */
    carrotage: {
        width: 150,
        minWidth: 150,
        autoWidth: 150,
        widthType: WidthType.Auto,
    },

    /*
        Перфорация
    */
    wellServices: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Ост. нефтенас.
    */
    cementBondLogging: {
        width: 130,
        minWidth: 130,
        autoWidth: 130,
        widthType: WidthType.Auto,
    },
    /*
        ГРП
    */
    sample: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        ГДИ
    */
    fluidTest: {
        width: 60,
        minWidth: 60,
        autoWidth: 60,
        widthType: WidthType.Auto,
    },
    /*
        Потокометрия
    */
    flowmetry: {
        width: 180,
        minWidth: 130,
        autoWidth: 180,
        widthType: WidthType.Auto,
        singleColumnMinWidth: 22,
    },
    /*
        Стратиграфия
    */
    stratigraphy: {
        width: 180,
        minWidth: 130,
        autoWidth: 180,
        widthType: WidthType.Auto,
        singleColumnMinWidth: 22,
    },
    /*
        ИННК / ИГН
    */
    innk: {
        width: 180,
        minWidth: 130,
        autoWidth: 180,
        widthType: WidthType.Auto,
        singleColumnMinWidth: 22,
    },
    /*
        Маркерные исследования
    */
    tracers: {
        width: 180,
        minWidth: 130,
        autoWidth: 180,
        widthType: WidthType.Auto,
        singleColumnMinWidth: 22,
    },
    /*
        Тех. характеристики
    */
    tech: {
        width: 120,
        minWidth: 120,
        autoWidth: 120,
        widthType: WidthType.Auto,
    },

    tech_bridges: {
        width: 120,
        minWidth: 120,
        autoWidth: 120,
        widthType: WidthType.Auto,
    },

    tech_cement: {
        width: 120,
        minWidth: 120,
        autoWidth: 120,
        widthType: WidthType.Auto,
    },

    tech_columns: {
        width: 120,
        minWidth: 120,
        autoWidth: 120,
        widthType: WidthType.Auto,
    },
    /*
        Литология
    */
    lithology: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Колонка для Всех гео объектов
        Возможные другие названия: stratum
    */
    allGeoObjects: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Колонка для РИГИС и ФГИ
        Возможные другие названия: stratum
    */
    geoObjects: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },

    /*
        Гистограммы параметров
    */
    paramsHistogram: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Цементометрия
    */
    cementometry: {
        width: 64,
        minWidth: 64,
        autoWidth: 64,
        widthType: WidthType.Auto,
    },
    /*
        Опробования пластов
    */
    testing: {
        width: 128,
        minWidth: 64,
        autoWidth: 128,
        widthType: WidthType.Auto,
    },
    /*
        Эффективная толщина
    */
    thicknessInterlayer: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Керн
    */
    core: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    /*
        Керн параметры
    */
    coreParamsHistogram: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },

    /*
        Нефтегазонасыщенность
    */
    stratigraphy_tier: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    stratigraphy_horizon: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    stratigraphy_overhorizon: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    stratigraphy_department: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
    stratigraphy_system: {
        width: 32,
        minWidth: 32,
        autoWidth: 32,
        widthType: WidthType.Auto,
    },
};

/*
    Словарь соответствий колонок и параметра paramColumn.
    paramColumn - это значение, которое приходит вместе с колонкой с backend-a.
*/
export const paramColumnToColumnKey: Record<number, TabletColumns> = {
    /*
        Перфорация
    */
    1: 'wellServices',
    /*
        Пласт
    */
    2: 'geoObjects',
    /*
        Литология
    */
    3: 'lithology',
    /*
        Параметры
    */
    5: 'paramsHistogram',
    /*
        Цементометрия
    */
    6: 'cementometry',
    /*
        Каротаж
    */
    7: 'carrotage',
    /*
        Керн
    */
    8: 'core',
    /*
        Опробование
    */
    9: 'testing',

    /*
        Потокометрия
    */
    10: 'flowmetry',
    /*
        ГСР
        11: ''
    */
    /*
        Пакеры
        12: ''
    */
    /*
        ГДИ
    */
    13: 'fluidTest',
    /*
        Реперы
        14: ''
    */
    /*
        ГРП
    */
    15: 'sample',
    /*
        Остаточная нефтенасыщенность
    */
    16: 'cementBondLogging',
    /*
        Стратиграфия
    */
    17: 'stratigraphy',
    /*
        Тех. характеристики
    */
    18: 'tech',
    /*
        Эффективная толщина
    */
    19: 'thicknessInterlayer',
    /*
        ИННК / ИГН
    */
    21: 'innk',
    /*
        Маркерные исследования
    */
    22: 'tracers',
    /*
        РИГИС
    */
    23: 'allGeoObjects',
    /*
        Цем. мосты
    */
    24: 'tech_bridges',
    /*
        Цементаж
    */
    25: 'tech_cement',
    /*
        Колонны
    */
    26: 'tech_columns',
    /*
        По керну
    */
    27: 'coreParamsHistogram',
};

/*
    Значения настроек кривых по умолчанию
*/
export const defaultCurveDisplaySettings: CurveDisplaySettings = {
    /*
        Зеркальное отображение кривой
    */
    isMirroring: false,
    /*
        Десятичный логарифм значений
    */
    isDecimalLog: false,
    /*
        Ограничение глубин кривой
    */
    isChangeMinMaxDepths: false,
    minMaxDepths: null,
    /*
        Изменение граничных точек
    */
    isChangeMinMaxValues: false,
    minMaxValueType: MinMaxValueType.MinMax,
    minMaxValues: null,
    /*
        Заливка области диаграммы
    */
    isFilling: false,
    fillingType: FillingType.Value,

    fillingValue: 0,
    fillingValueType: FillingValueType.Less,
    isFillingOutside: false,

    // Заливка между парой кривых
    isCurvesFilling: false,
    curvesFillingType: CurvesFillingType.None,
    curvesFillingColor: '#d4d4d4',
    curvesFillingResearch: undefined,
    isScales: false,
    isCurveStep: false,
    maxStep: 0,
    childrenCurves: null,
    scaleProps: null,
};
